import type { EffectDefinition, EffectContext } from './types'

interface Laser {
  x: number
  y: number
  angle: number
  length: number
  speed: number
  width: number
  opacity: number
  life: number
  maxLife: number
  r: number
  g: number
  b: number
}

interface LaserState {
  lasers: Laser[]
  nextSpawn: number
}

const COLORS = [
  [255, 40, 80],
  [40, 200, 255],
  [120, 80, 255],
  [255, 120, 40],
  [40, 255, 150]
]

function createLaser(width: number, height: number): Laser {
  const fromEdge = Math.floor(Math.random() * 4)
  let x: number, y: number, angle: number

  if (fromEdge === 0) { // top
    x = Math.random() * width
    y = -10
    angle = Math.PI / 4 + Math.random() * Math.PI / 2
  } else if (fromEdge === 1) { // right
    x = width + 10
    y = Math.random() * height
    angle = Math.PI * 0.6 + Math.random() * Math.PI * 0.8
  } else if (fromEdge === 2) { // bottom
    x = Math.random() * width
    y = height + 10
    angle = -Math.PI / 4 - Math.random() * Math.PI / 2
  } else { // left
    x = -10
    y = Math.random() * height
    angle = -Math.PI * 0.4 + Math.random() * Math.PI * 0.8
  }

  const color = COLORS[Math.floor(Math.random() * COLORS.length)]!
  const life = 0.8 + Math.random() * 1.5

  return {
    x, y, angle,
    length: 100 + Math.random() * 300,
    speed: 300 + Math.random() * 400,
    width: 1 + Math.random() * 2,
    opacity: 0.15 + Math.random() * 0.25,
    life,
    maxLife: life,
    r: color[0]!,
    g: color[1]!,
    b: color[2]!
  }
}

export const lasers: EffectDefinition = {
  id: 'lasers',
  name: 'Lasers',

  init(_ctx: EffectContext): LaserState {
    return {
      lasers: [],
      nextSpawn: 0
    }
  },

  update(state: unknown, ctx: EffectContext) {
    const s = state as LaserState
    const dt = ctx.deltaTime / 1000

    s.nextSpawn -= dt
    if (s.nextSpawn <= 0) {
      s.lasers.push(createLaser(ctx.width, ctx.height))
      s.nextSpawn = 0.1 + Math.random() * 0.3
    }

    for (let i = s.lasers.length - 1; i >= 0; i--) {
      const laser = s.lasers[i]!
      laser.x += Math.cos(laser.angle) * laser.speed * dt
      laser.y += Math.sin(laser.angle) * laser.speed * dt
      laser.life -= dt
      if (laser.life <= 0) s.lasers.splice(i, 1)
    }

    if (s.lasers.length > 50) {
      s.lasers.splice(0, s.lasers.length - 50)
    }
  },

  draw(state: unknown, ctx: EffectContext) {
    const s = state as LaserState
    const { ctx: c } = ctx

    for (const laser of s.lasers) {
      const fade = Math.min(1, laser.life / laser.maxLife * 2)
      const alpha = laser.opacity * fade

      const endX = laser.x - Math.cos(laser.angle) * laser.length
      const endY = laser.y - Math.sin(laser.angle) * laser.length

      // Glow
      c.beginPath()
      c.moveTo(laser.x, laser.y)
      c.lineTo(endX, endY)
      c.strokeStyle = `rgba(${laser.r}, ${laser.g}, ${laser.b}, ${alpha * 0.3})`
      c.lineWidth = laser.width * 4
      c.stroke()

      // Core beam
      c.beginPath()
      c.moveTo(laser.x, laser.y)
      c.lineTo(endX, endY)
      c.strokeStyle = `rgba(${laser.r}, ${laser.g}, ${laser.b}, ${alpha})`
      c.lineWidth = laser.width
      c.stroke()

      // Bright center
      c.beginPath()
      c.moveTo(laser.x, laser.y)
      c.lineTo(endX, endY)
      c.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.4})`
      c.lineWidth = laser.width * 0.3
      c.stroke()
    }
  }
}
